import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { TpETLObjectTypes } from '../../etl-data-types/interfaces';
import { TpETLComponentTypeManifest } from '../interfaces';
import { TpETLPipelineComponent, TpETLDryRun, TpETLDryRunComponentResult } from '../../interfaces';
import { addDryRunObjectTable, getETLComponentInputObject } from '../../utils';
import { addETLDryRunError } from '../../etlDryRun';
import { CONFIGPARAMTYPES } from '../../../visual-analytics/helpers/configParams';
import { VALTYPES } from '../../../../data/interfaces';
import { createConfigParamTypeNewColumn, createConfigParamTypeExistingMatrixColumnList } from '../utils';

function performDryRun(dryRun: TpETLDryRun, comp: TpETLPipelineComponent): TpETLDryRunComponentResult {
  const tableObj = addDryRunObjectTable(dryRun, comp.outputObjectIds['observationTable'], comp);

  const matrix = getETLComponentInputObject(dryRun, comp, 'source');
  if (matrix.type !== TpETLObjectTypes.MATRIX) {
    addETLDryRunError(dryRun, `Input is not a matrix`, comp.id, 'source');
    return { abort: false };
  }

  // Add the metadata columns to the output table
  const selectedColumns = (comp.params['metadataColumns'] ?? []) as string[];
  const baseColumns = matrix.obsMetadataColumns.map((col) => structuredClone(col));
  tableObj.columns = baseColumns.filter((col) => selectedColumns.includes(col.identifier));

  let paramErrors = false;

  // Add the feature columns
  const featureCount = parseInt(comp.params['featureCount']);
  if (featureCount <= 0 || isNaN(featureCount)) {
    addETLDryRunError(dryRun, `Count must be positive`, comp.id, 'featureCount');
    paramErrors = true;
  }
  const paramVal = comp.params['columnData'];
  if (!paramVal || !paramVal.identifier || !paramVal.name) {
    addETLDryRunError(dryRun, `ColumnData identifier and name are required`, comp.id, 'columnData');
    paramErrors = true;
  }

  if (paramErrors) {
    return {
      abort: false,
    };
  }

  const featureColumns = [];
  for (let i = 1; i < featureCount + 1; i++) {
    featureColumns.push({
      identifier: `${paramVal.identifier}_${i}`,
      name: `${paramVal.name} ${i}`,
      description: paramVal.description || '',
      dataType: VALTYPES.NUMBER,
    });
  }
  tableObj.columns = [...tableObj.columns, ...featureColumns];

  return {
    abort: false,
  };
}

export const manifestETLMatrixExtractFeatures: TpETLComponentTypeManifest = {
  id: 'MatrixExtractFeatures',
  name: 'Extract feature observations',
  description: 'Extract a set of feature observations from a matrix.',
  categoryId: 'matrix',
  icon: <FontAwesomeIcon flip="horizontal" icon={['fad', 'arrows-split-up-and-left']} />,
  performDryRun,
  inputs: [
    {
      id: 'source',
      name: 'selected matrix',
      type: TpETLObjectTypes.MATRIX,
      description: 'Matrix containing feature observations to extract',
      obsMetadataColumns: [],
      featureMetadataColumns: [],
    },
  ],
  outputs: [
    {
      type: TpETLObjectTypes.TABLE,
      id: 'observationTable',
      name: 'feature observations table',
      columns: [],
    },
  ],
  params: [
    {
      id: 'featureCount',
      name: 'Feature Count',
      description: 'The first count features will be extracted.',
      paramType: {
        configParamTypeId: CONFIGPARAMTYPES.Numerical,
        defaultValue: 0,
      },
    },
    {
      id: 'columnData',
      name: 'Feature column name prefix',
      description: 'Prefix used for all extracted feature columns. Will be followed by an index.',
      paramType: createConfigParamTypeNewColumn(VALTYPES.NUMBER),
    },
    {
      id: 'metadataColumns',
      name: 'Metadata columns',
      description: 'Metadata columns to include in the output table.',
      paramType: createConfigParamTypeExistingMatrixColumnList('source', 'observations'),
    },
  ],
};
